
import { useEffect, useRef, useCallback } from 'react';

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

let isMonitoringActive = false;

export const useOptimizedPerformanceMonitoring = () => {
  const observersRef = useRef<PerformanceObserver[]>([]);
  const metricsRef = useRef<Map<string, number>>(new Map());

  const reportMetric = useCallback((name: string, value: number) => {
    // Debounce metrics reporting
    const lastValue = metricsRef.current.get(name);
    if (lastValue && Math.abs(value - lastValue) < 10) return;
    
    metricsRef.current.set(name, value);
    // Metric recorded silently
    
    if (window.gtag) {
      window.gtag('event', 'web_vital', {
        name,
        value: Math.round(value),
        event_category: 'Performance'
      });
    }
  }, []);

  useEffect(() => {
    // Only run performance monitoring once per app instance and in production
    if (process.env.NODE_ENV !== 'production' || isMonitoringActive || typeof window === 'undefined') {
      return;
    }
    
    isMonitoringActive = true;

    try {
      // FCP Observer
      const fcpObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.name === 'first-contentful-paint') {
            reportMetric('FCP', entry.startTime);
          }
        }
      });
      fcpObserver.observe({ entryTypes: ['paint'] });
      observersRef.current.push(fcpObserver);

      // LCP Observer
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        reportMetric('LCP', lastEntry.startTime);
      });
      lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
      observersRef.current.push(lcpObserver);

      // CLS Observer (throttled)
      let clsValue = 0;
      let clsTimeout: NodeJS.Timeout;
      
      const clsObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (!(entry as any).hadRecentInput) {
            clsValue += (entry as any).value;
          }
        }
        
        clearTimeout(clsTimeout);
        clsTimeout = setTimeout(() => {
          reportMetric('CLS', clsValue * 1000);
        }, 500);
      });
      clsObserver.observe({ entryTypes: ['layout-shift'] });
      observersRef.current.push(clsObserver);

    } catch (error) {
      console.warn('Performance monitoring not supported:', error);
    }

    return () => {
      observersRef.current.forEach(observer => observer.disconnect());
      observersRef.current = [];
      isMonitoringActive = false;
    };
  }, [reportMetric]);
};
